import merge from 'deepmerge';
import { createTheme, Theme } from '@material-ui/core/styles';
import { ThemeOptions } from '@material-ui/core/styles/createTheme';
import { PaletteOptions } from '@material-ui/core/styles/createPalette';
import { themeTypePalettes } from './DataTypes';

declare module '@material-ui/core/styles/createPalette' {
  interface Palette {
    neutral: {
      light: string;
      main: string;
      dark: string;
      contrastText: string;
    };
    neutralLight: {
      light: string;
      main: string;
      dark: string;
      contrastText: string;
    };
    navBarPrimary: {
      light: string;
      main: string;
      dark: string;
      contrastText: string;
    };
    navBarSecondary: {
      light: string;
      main: string;
      dark: string;
      contrastText: string;
    };
    dialogPrimary: {
      light: string;
      main: string;
      dark: string;
      contrastText: string;
    };
    gridSelect: {
      light: string;
      main: string;
      dark: string;
      contrastText: string;
    };
    success: {
      light: string;
      main: string;
      dark: string;
      contrastText: string;
    };
    info: {
      light: string;
      main: string;
      dark: string;
      contrastText: string;
    };
    warning: {
      light: string;
      main: string;
      dark: string;
      contrastText: string;
    };
    highlight: {
      light: string;
      main: string;
      dark: string;
      contrastText: string;
    };
  }
}

const defaultThemeTypography: any = {
  fontSize: 14,
};

const applicationContainer = () => document.getElementById('application-root');

const defaultThemeProps: any = {
  MuiModal: {
    container: applicationContainer,
  },
  MuiPopover: {
    container: applicationContainer,
  },
  MuiDialog: {
    container: applicationContainer,
  },
  MuiMenu: {
    container: applicationContainer,
  },
  MuiList: {
    dense: true,
    disablePadding: true,
  },
  MuiMenuItem: {
    // divider: true,
  },
  MuiCheckbox: {
    color: 'primary',
  },
};

export default function createWorkSightBaseTheme(options: ThemeOptions = {}): Theme {
  const defaultThemeOptions: ThemeOptions = {
    palette: themeTypePalettes.default as PaletteOptions,
    typography: defaultThemeTypography,
    props: defaultThemeProps,
    fontSizes: {},
  };

  // need to create theme first to get access to augmentColor (silly but Material-ui doesn't want to expose it on its own)
  const workSightThemeOptions: ThemeOptions = merge(defaultThemeOptions, options);
  const baseTheme = createTheme(workSightThemeOptions);

  // add light, dark, and contrastText color values for custom colors.
  baseTheme.palette.neutral = baseTheme.palette.augmentColor({ ...baseTheme.palette.neutral });
  baseTheme.palette.neutralLight = baseTheme.palette.augmentColor({ ...baseTheme.palette.neutralLight });
  baseTheme.palette.navBarPrimary = baseTheme.palette.augmentColor({ ...baseTheme.palette.navBarPrimary });
  baseTheme.palette.navBarSecondary = baseTheme.palette.augmentColor({ ...baseTheme.palette.navBarSecondary });
  baseTheme.palette.dialogPrimary = baseTheme.palette.augmentColor({ ...baseTheme.palette.dialogPrimary });
  baseTheme.palette.gridSelect = baseTheme.palette.augmentColor({ ...baseTheme.palette.gridSelect });
  baseTheme.palette.success = baseTheme.palette.augmentColor({ ...baseTheme.palette.success });
  baseTheme.palette.info = baseTheme.palette.augmentColor({ ...baseTheme.palette.info });
  baseTheme.palette.warning = baseTheme.palette.augmentColor({ ...baseTheme.palette.warning });
  baseTheme.palette.highlight = baseTheme.palette.augmentColor({ ...baseTheme.palette.highlight });
  baseTheme.palette.text.disabled = '#00000070';

  baseTheme.overrides = merge(baseTheme.overrides || {}, {
    MuiPaper: {
      root: {
        color: 'inherit',
      },
    },
    MuiDrawer: {
      paperAnchorDockedLeft: {
        borderRight: 'none',
      },
    },
    MuiInputBase: {
      root: {
        color: 'inherit',
      },
    },
    MuiInput: {
      root: {
        color: 'inherit',
      },
      underline: {
        '&::after': {
          borderColor: baseTheme.palette!.dialogPrimary.main,
        },
      },
    },
    MuiOutlinedInput: {
      root: {
        '&$focused $notchedOutline': {
          borderColor: baseTheme.palette.dialogPrimary.main,
        },
      },
    },
    MuiFormLabel: {
      root: {
        '&$focused': {
          color: baseTheme.palette!.dialogPrimary.main,
        },
      },
    },
    MuiTab: {
      root: {
        textAlign: 'center',
      },
    },
    MuiTablePagination: {
      root: {
        color: 'inherit',
      },
      selectRoot: {
        marginLeft: '2px',
        marginRight: '20px',
      },
      select: {
        paddingBottom: '4px',
      },
    },
    MuiMenuItem: {
      root: {
        height: 'auto',
        minHeight: 'auto',
        color: 'inherit',
        '&:hover': {
          backgroundColor: baseTheme.palette!.neutralLight.light,
        },
        '&$selected, &:active, &:focus, &:active:focus': {
          backgroundColor: baseTheme.palette!.dialogPrimary.main,
          color: baseTheme.palette!.dialogPrimary!.contrastText,
          '& > *': {
            color: baseTheme.palette!.dialogPrimary!.contrastText,
          },
          '&:hover, &:focus': {
            backgroundColor: baseTheme.palette!.dialogPrimary.main,
          },
        },
      },
    },
    MuiListItem: {
      divider: {
        '&:last-child': {
          borderBottom: 'none',
        },
      },
    },
    MuiListItemIcon: {
      root: {
        color: 'inherit',
      },
    },
    MuiListItemText: {
      root: {
        marginTop: '2px',
        marginBottom: '2px',
      },
    },
    MuiListSubheader: {
      root: {
        backgroundColor: baseTheme.palette.neutralLight.main,
        color: `${baseTheme.palette.neutralLight.contrastText} !important`,
      },
      sticky: {
        backgroundColor: baseTheme.palette!.neutralLight.main,
        color: `${baseTheme.palette.neutralLight.contrastText} !important`,
      },
    },
    MuiAccordion: {
      root: {
        '&:before': {
          height: 0,
        },
      },
    },
    MuiAccordionSummary: {
      expandIcon: {
        '&$expanded': {
          transform: 'rotate(90deg)',
        },
      },
    },
    MuiButton: {
      root: {
        color: 'inherit',
        borderRadius: '4px',
        // textTransform: 'none',
        minHeight: '0px',
        lineHeight: '1.4',
        whiteSpace: 'nowrap',
      },
      contained: {
        boxShadow: 'none',
        color: 'inherit',
      },
      outlined: {
        borderColor: baseTheme.palette!.neutral.main,
        color: 'inherit',
      },
    },
    MuiIconButton: {
      root: {
        color: 'inherit',
        padding: '0px',
      },
    },
    MuiCheckbox: {
      root: {
        color: baseTheme.palette!.dialogPrimary.main,
        '&$checked': {
          color: baseTheme.palette!.dialogPrimary.main,
        },
      },
      colorPrimary: {
        color: baseTheme.palette!.dialogPrimary.main,
        '&$checked': {
          color: baseTheme.palette!.dialogPrimary.main,
        },
      },
    },
    MuiSwitch: {
      root: {
        color: baseTheme.palette!.dialogPrimary.main,
      },
      switchBase: {
        width: 'auto',
        height: 'auto',
      },
    },
    MuiSvgIcon: {
      root: {
        fontSize: '1.5rem',
      },
    },
    MuiLink: {
      root: {
        color: baseTheme.palette.dialogPrimary.main,
        '&:visited': {
          color: baseTheme.palette.dialogPrimary.main,
        },
        '&:hover, &:active': {
          color: baseTheme.palette.secondary.main,
        },
      },
    },
    MuiPickersCalendarHeader: {
      transitionContainer: {
        height: '1.5rem',
      },
      iconButton: {
        padding: '12px',
      },
    },
  });

  return baseTheme;
}
